import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Activity, ArrowRight, ChevronDown, Code, ExternalLink, FileText, Globe, HardDrive, Mail, Menu, Rocket, Server, Shield, ShieldCheck, Users, X } from 'lucide-react';
import PulseDot from '@/components/atoms/PulseDot';
import Button from '@/components/atoms/Button';
import BrandMark from '@/components/atoms/BrandMark';

// ─────────────────────────────────────────────────────────────────────────────
// Tactical Feature Gate — 2026-06-21
// ─────────────────────────────────────────────────────────────────────────────
// `status` per surface drives the chip + CTA:
//   'live'    — provably live in production with verifiable receipts. No chip,
//               normal href, normal CTA. Customer can click → use today.
//   'phase-a' — HMAC-SHA256 attestation working today; ML-DSA-65 Phase B
//               target when noble-pq WASM lands in CF Workers (~Q3 2026 per
//               NIST FIPS-204 ecosystem rollout). Chip clarifies the bound.
//   'beta'    — sovereign enterprise pilot only. CTA goes to a mailto
//               waitlist (uses our own sovereign inbound email pipeline,
//               which IS live on @drklynx.com — proven this session).
//               No public signup path until the surface is productized.
//
// This is load-bearing security architecture, not marketing polish: it
// prevents customer signups for surfaces we can't yet attest to.
// (Per ESME 8/8 vote + Gemini audit, 2026-06-21.)
// ─────────────────────────────────────────────────────────────────────────────
const BETA_MAILTO = (productLabel) =>
  `mailto:enterprise@drklynx.com?subject=${encodeURIComponent(productLabel + ' — sovereign enterprise pilot access')}` +
  `&body=${encodeURIComponent('I represent a sovereign enterprise cluster interested in early ' + productLabel + ' access. Please share onboarding requirements + technical attestation expectations.\n\nOrganization:\nContact:\nUse case:\n')}`;

const productsMega = [
  { heading: 'Suite', items: [
    { label: 'ZbraLynX',  desc: 'Sovereign CRM · the data layer',         icon: Users,    href: BETA_MAILTO('ZbraLynX'),   status: 'beta' },
    { label: 'DrkLynX Weaver', desc: 'Sovereign PR Sentinel · build governance', icon: Code,   href: '/products/weaver',             status: 'live' },
    { label: 'DrkLynX Signal', desc: 'Sovereign email & comms',                icon: Mail,     href: BETA_MAILTO('DrkLynX Signal'),  status: 'beta' },
    { label: 'DrkLynX Archive',desc: 'Sovereign quantum-resistant storage',    icon: HardDrive,href: BETA_MAILTO('DrkLynX Archive'), status: 'beta' },
  ]},
  { heading: 'Trust Layer', items: [
    { label: 'DrkLynX Trust',   desc: 'Cryptographic clearing house',           icon: ShieldCheck, href: '/trust',           status: 'phase-a' },
    { label: 'DrkLynX Ledger',  desc: 'Settlement & transaction record',        icon: FileText,    href: '/products/ledger', status: 'phase-a' },
    { label: 'DrkLynX Crossing',desc: 'Sovereign migration from any SaaS',      icon: Rocket,      href: BETA_MAILTO('DrkLynX Crossing'), status: 'beta' },
  ]},
  { heading: 'Infrastructure', items: [
    { label: 'DrkLynX Hosting',       desc: 'Managed sovereign hosting · Starter to Dedicated', icon: Server,   href: '/products/hosting', status: 'live' },
    { label: 'DrkLynX Domains',       desc: 'Sovereign registration · attested receipts',        icon: Globe,    href: '/domains',          status: 'live' },
    { label: 'Sovereign Edge Mesh',   desc: '~1,400 zones · attested control plane',             icon: Activity, href: '/trust',            status: 'phase-a' },
  ]},
];

// Status chip metadata. Rendered inline in the mega-menu card.
const STATUS_CHIP = {
  live:      null, // no chip — implicit "live"
  'phase-a': { label: 'Phase A · HMAC-SHA256', className: 'text-cyan bg-cyan/10 border-cyan/20' },
  beta:      { label: 'Sovereign Pilot',       className: 'text-gold bg-gold/10 border-gold/20' },
};

const whyMega = [
  { label: 'Quantum-Resistant Architecture', href: '/why/post-quantum', icon: Shield },
  { label: 'Sovereign Data Doctrine', href: '/why/sovereignty', icon: FileText },
  { label: 'Ledger Pulse', href: '/ledger-pulse', icon: Activity },
  { label: 'Compare DrkLynX', href: '/resources/compare', icon: ExternalLink },
];

const resourcesMega = [
  { label: 'Documentation', href: '/docs', icon: FileText },
  { label: 'Blog', href: '/resources/blog', icon: FileText },
  { label: 'Status', href: '/status', icon: Activity },
  { label: 'Trust Center', href: '/trust', icon: Shield },
  { label: 'About DrkLynX', href: '/about', icon: Users },
];

const MegaMenu = ({ items, columns, open }) => {
  if (!open) return null;
  return (
    <div className="absolute top-full left-0 right-0 glass-strong border-b border-signal/10 z-50 animate-in fade-in slide-in-from-top-2">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {columns ? (
          <div className="grid grid-cols-4 gap-8">
            {items.map((col) => (
              <div key={col.heading}>
                <div className="font-mono text-[10px] tracking-wider uppercase text-steel-muted mb-3">{col.heading}</div>
                {col.items.map((item) => {
                  // mailto: hrefs cannot use React Router <Link> — fall back to native <a>.
                  // Detected by scheme prefix so future mailto/tel/sms routes work too.
                  const isExternal = /^(mailto:|tel:|sms:|https?:)/.test(item.href);
                  const LinkEl = isExternal ? 'a' : Link;
                  const linkProps = isExternal ? { href: item.href } : { to: item.href };
                  const chip = STATUS_CHIP[item.status];
                  return (
                    <LinkEl
                      key={item.href + item.label}
                      {...linkProps}
                      data-testid={`mega-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className="flex items-start gap-3 p-2 -mx-2 rounded-md hover:bg-signal/5 transition-colors duration-fast group"
                    >
                      <item.icon className="w-5 h-5 text-green mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-signal text-sm font-medium group-hover:text-cyan transition-colors duration-fast">{item.label}</span>
                          {chip && (
                            <span className={`inline-flex items-center font-mono text-[9px] tracking-wider uppercase border px-1.5 py-0.5 rounded-sm leading-none ${chip.className}`}>
                              {chip.label}
                            </span>
                          )}
                        </div>
                        <div className="text-steel-muted text-xs">{item.desc}</div>
                      </div>
                    </LinkEl>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {items.map((item) => (
              <Link key={item.href} to={item.href} className="flex items-center gap-3 p-3 rounded-md hover:bg-signal/5 transition-colors duration-fast group">
                <item.icon className="w-4 h-4 text-green shrink-0" />
                <span className="text-signal text-sm group-hover:text-cyan transition-colors duration-fast">{item.label}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => { setMobileOpen(false); setActiveMenu(null); }, [location]);

  const openMenu = (menu) => { clearTimeout(timeoutRef.current); setActiveMenu(menu); };
  const closeMenu = () => { timeoutRef.current = setTimeout(() => setActiveMenu(null), 150); };

  return (
    <header className={`fixed top-8 left-0 right-0 z-40 transition-all duration-base ${scrolled ? 'glass-strong border-b border-signal/10' : ''}`}>
      <nav className="max-w-7xl mx-auto px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0" aria-label="DrkLynX Home">
            <img src="/logo-drklynx-sm.png" alt="DrkLynX" className="w-20 h-20 rounded-md drop-shadow-lg" />
            <BrandMark size="xl" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Products */}
            <div onMouseEnter={() => openMenu('products')} onMouseLeave={closeMenu}>
              <button data-testid="nav-products" className="flex items-center gap-1 px-3 py-2 text-sm text-steel hover:text-signal transition-colors duration-fast rounded-md">
                Products <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-fast ${activeMenu === 'products' ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {/* Why */}
            <div onMouseEnter={() => openMenu('why')} onMouseLeave={closeMenu}>
              <button data-testid="nav-why" className="flex items-center gap-1 px-3 py-2 text-sm text-steel hover:text-signal transition-colors duration-fast rounded-md">
                Why DrkLynX <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-fast ${activeMenu === 'why' ? 'rotate-180' : ''}`} />
              </button>
            </div>
            <Link to="/ledger-pulse" data-testid="nav-ledger-pulse" className="px-3 py-2 text-sm text-green hover:text-signal transition-colors duration-fast rounded-md">
              Ledger Pulse
            </Link>
            {/* Resources */}
            <div onMouseEnter={() => openMenu('resources')} onMouseLeave={closeMenu}>
              <button data-testid="nav-resources" className="flex items-center gap-1 px-3 py-2 text-sm text-steel hover:text-signal transition-colors duration-fast rounded-md">
                Resources <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-fast ${activeMenu === 'resources' ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-signin" className="hidden sm:block text-sm text-steel hover:text-signal transition-colors duration-fast">Sign In</Link>
            <Link to="/pricing">
              <Button size="sm" data-testid="nav-cta">Start Free <ArrowRight className="w-3.5 h-3.5" /></Button>
            </Link>
            <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-signal" aria-label="Toggle menu">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mega menus */}
      <div onMouseEnter={() => openMenu(activeMenu)} onMouseLeave={closeMenu}>
        <MegaMenu items={productsMega} columns open={activeMenu === 'products'} />
        <MegaMenu items={whyMega} open={activeMenu === 'why'} />
        <MegaMenu items={resourcesMega} open={activeMenu === 'resources'} />
      </div>

      {/* Mobile */}
      {mobileOpen && (
        <div data-testid="mobile-nav" className="lg:hidden glass-strong border-t border-signal/10 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-6 space-y-4">
            <div className="font-mono text-[10px] text-steel-muted uppercase tracking-wider">Products</div>
            {productsMega.flatMap(c => c.items).map(i => (
              <Link key={i.href} to={i.href} className="flex items-center gap-2 py-2 text-sm text-steel hover:text-cyan transition-colors">{i.label}</Link>
            ))}
            <div className="h-px bg-signal/10" />
            <Link to="/ledger-pulse" className="block py-2 text-sm text-cyan">Ledger Pulse</Link>
            <Link to="/pricing" className="block py-2 text-sm text-steel hover:text-signal">Pricing</Link>
            <Link to="/resources/blog" className="block py-2 text-sm text-steel hover:text-signal">Blog</Link>
            <Link to="/docs" className="block py-2 text-sm text-steel hover:text-signal">Docs</Link>
            <Link to="/trust" className="block py-2 text-sm text-steel hover:text-signal">Trust Center</Link>
            <div className="h-px bg-signal/10" />
            <Link to="/login" className="block py-2 text-sm text-steel hover:text-signal">Sign In</Link>
            <Link to="/pricing"><Button className="w-full">Start Free <ArrowRight className="w-3.5 h-3.5" /></Button></Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default NavBar;
